import { useApi } from '@/hooks/useApi';
import {
  Package,
  Plus,
  Archive,
  AlertCircle,
  XCircle,
  Loader2,
} from 'lucide-react';

interface InventoryItem {
  id: string;
  name: string;
  sku: string;
  category: string;
  quantity: number;
  reorderPoint: number;
  unitCost: number;
  status: 'in_stock' | 'low_stock' | 'out_of_stock';
}

interface InventoryData {
  items: InventoryItem[];
  totalItems: number;
  inStock: number;
  lowStock: number;
  outOfStock: number;
}

const STATUS_STYLES: Record<string, { class: string; label: string }> = {
  in_stock: { class: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', label: 'In Stock' },
  low_stock: { class: 'bg-amber-500/10 text-amber-400 border-amber-500/30', label: 'Low Stock' },
  out_of_stock: { class: 'bg-red-500/10 text-red-400 border-red-500/30', label: 'Out of Stock' },
};

export default function InventoryPage() {
  const { data, isLoading } = useApi<InventoryData>('/api/inventory');

  const items = data?.items ?? [];
  const totalItems = data?.totalItems ?? 0;
  const inStock = data?.inStock ?? 0;
  const lowStock = data?.lowStock ?? 0;
  const outOfStock = data?.outOfStock ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10">
            <Package className="w-6 h-6 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Inventory</h1>
            <p className="text-sm text-muted-foreground">Parts and supplies management</p>
          </div>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-foreground text-sm font-medium rounded-lg transition-colors">
          <Plus className="w-4 h-4" />
          Add Item
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={Package} label="Total Items" value={totalItems} />
        <MetricCard icon={Archive} label="In Stock" value={inStock} accent />
        <MetricCard icon={AlertCircle} label="Low Stock" value={lowStock} warn />
        <MetricCard icon={XCircle} label="Out of Stock" value={outOfStock} critical />
      </div>

      {/* Table */}
      <div className="bg-card/50 backdrop-blur border border-border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No inventory items</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Name</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">SKU</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Category</th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Quantity</th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Reorder Point</th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Unit Cost</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {items.map((item) => {
                  const st = STATUS_STYLES[item.status] ?? STATUS_STYLES['in_stock']!;
                  return (
                    <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-foreground">{item.name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-muted-foreground font-mono">{item.sku}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground">{item.category}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-mono ${item.quantity <= item.reorderPoint ? 'text-amber-400' : 'text-foreground'}`}>
                          {item.quantity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-muted-foreground font-mono">{item.reorderPoint}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-foreground font-mono">${item.unitCost.toFixed(2)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${st.class}`}>
                          {st.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  accent,
  warn,
  critical,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  accent?: boolean;
  warn?: boolean;
  critical?: boolean;
}) {
  return (
    <div className="bg-card/50 backdrop-blur border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <span
        className={`text-2xl font-bold ${
          accent ? 'text-emerald-400' : warn ? 'text-amber-400' : critical ? 'text-red-400' : 'text-foreground'
        }`}
      >
        {value}
      </span>
    </div>
  );
}
